import { useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiArrowUpRight, FiBookOpen, FiCheck, FiStar } from "react-icons/fi";
import CatalogueCover from "./CatalogueCover";
import CatalogueBookActions from "./CatalogueBookActions";
import BookRevealShell from "./BookRevealShell";
import useBookRevealPresence from "./useBookRevealPresence";
import { catalogueRoute } from "./booksQueryParams";
import { catalogueBookPath, enabled, isInPersonalLibrary } from "./catalogueBookDetails";
import useAuthStore from "store/authStore";
import "./bookReveal.css";

const readableDescription = (value) => {
	if (!value) return "";
	const doc = new DOMParser().parseFromString(String(value), "text/html");
	doc.querySelectorAll("script, style").forEach((node) => node.remove());
	const text = doc.body.textContent.replace(/\s+/g, " ").trim();
	return text.length > 480 ? `${text.slice(0, 480).replace(/\s+\S*$/, "")}…` : text;
};

export default function CatalogueQuickLook({ book, status, onRetry, onClose, position = 0, count = 1, onPrevious, onNext }) {
	const location = useLocation();
	const loggedIn = useAuthStore((state) => state.loggedIn);
	const route = catalogueRoute(location);
	const snapshot = useRef({ position, count, status });
	if (book) snapshot.current = { position, count, status };
	const reveal = useBookRevealPresence(book, route);
	if (!reveal.shown) return null;
	const title = reveal.book;
	const description = readableDescription(title.description);
	const rating = Number(title.rating);
	const rated = rating > 0 && rating <= 5;
	const publisher = typeof title.publisher === "string" ? title.publisher : title.publisher?.name;
	const feedback = snapshot.current.status;
	return (
		<BookRevealShell closing={reveal.closing} reduced={reveal.reduced} onClose={onClose} {...snapshot.current} onPrevious={onPrevious} onNext={onNext}>
			{feedback ? <article className="book-reveal-stage book-reveal-feedback" aria-busy={feedback === "loading"}>
				<div className="book-reveal-details">
					<FiBookOpen aria-hidden="true" />
					<h2 id="book-reveal-heading">{title.title}</h2>
					<p role={feedback === "loading" ? "status" : "alert"}>{feedback === "loading" ? "Getting your book ready for a closer look." : feedback === "invalid" ? "This book link isn’t valid. Return to the catalogue to choose a book." : "The book may no longer be available, or there was a connection problem."}</p>
					<div className="book-reveal-links">{feedback === "error" && <button type="button" onClick={onRetry}>Try again</button>}<button type="button" onClick={onClose}>Keep browsing</button></div>
				</div>
			</article> : <article key={title.id} className="book-reveal-stage">
				<div className="book-reveal-cover"><CatalogueCover book={title} eager /></div>
				<div className="book-reveal-details">
					<p className="book-reveal-eyebrow">{title.category?.name || "Explore the library"}</p>
					<h2 id="book-reveal-heading">{title.title}</h2>
					{title.author && <p className="book-reveal-author">by {title.author}</p>}
					<div className="book-reveal-rating"><span aria-hidden="true">{Array.from({ length: 5 }, (_, index) => <FiStar key={index} className={rated && index < Math.round(rating) ? "is-filled" : ""} />)}</span><p>{rated ? `${rating.toFixed(1)} out of 5` : "No ratings yet"}</p>{loggedIn && isInPersonalLibrary(title) && <span className="book-reveal-owned"><FiCheck aria-hidden="true" />In your library</span>}</div>
					{description && <p className="book-reveal-description">{description}</p>}
					{(publisher || title.isbn) && <dl className="book-reveal-metadata">{publisher && <div><dt>Publisher</dt><dd>{publisher}</dd></div>}{title.isbn && <div><dt>ISBN</dt><dd>{title.isbn}</dd></div>}</dl>}
					{enabled(title.has_preview) && <p className="book-reveal-preview"><FiBookOpen aria-hidden="true" />Preview available on the book page</p>}
					<CatalogueBookActions key={title.id} book={title} />
					<div className="book-reveal-links"><Link to={catalogueBookPath(title, location)}>View book<FiArrowUpRight aria-hidden="true" /></Link><button type="button" onClick={onClose}>Keep browsing</button></div>
				</div>
			</article>}
		</BookRevealShell>
	);
}
